(() => {
  'use strict';

  const PAGE_SIZE = 24;
  const INDEX_PAGE_SIZE = 250;
  const CARD_BATCH_SIZE = 12;
  const SORTS = new Set(['manual', 'created-descending', 'price-ascending', 'price-descending', 'title-ascending', 'title-descending']);
  const CATEGORIES = new Map([
    ['skincare', 'Skincare'], ['makeup', 'Makeup'], ['wellness', 'Wellness'],
    ['hair-body', 'Hair & Body'], ['baby-kids', 'Baby & Kids'], ['home', 'Home & Living'], ['other', 'Other']
  ]);
  const PARAMS = { category: 'nc_category', type: 'nc_type', vendor: 'filter.p.vendor', stock: 'filter.v.availability', min: 'filter.v.price.gte', max: 'filter.v.price.lte' };
  const PREVIEW_PARAMS = ['preview_theme_id', 'preview_key', '_fd', 'pb'];
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  const keyOf = value => String(value || '').trim().toLocaleLowerCase();
  const typeKeyOf = value => keyOf(value).replace(/[-_\s]+/g, ' ');
  const unique = values => [...new Set(values)];
  const priceValue = value => value !== null && String(value).trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? Math.round(Number(value) * 100) : null;
  const typeLabel = value => typeKeyOf(value).replace(/\b[a-z][a-z0-9]*\b/gi, word => /^(bb|cc|spf|pdrn|uv|aha|bha|pha|led|egf|dna|rna)$/i.test(word) ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1).toLowerCase());
  const cloneState = state => ({ ...state, category: [...state.category], type: [...state.type], vendor: [...state.vendor] });

  function initialise(root) {
    if (root.dataset.ncCatalogReady) return;
    root.dataset.ncCatalogReady = 'initialising';
    const grid = root.querySelector('[data-nc-grid]');
    const controls = root.querySelector('[data-nc-catalog-filters]');
    const status = root.querySelector('[data-nc-status]');
    const count = root.querySelector('[data-nc-count]');
    const more = root.querySelector('[data-nc-more]');
    const empty = root.querySelector('[data-nc-empty]');
    const fallback = root.querySelector('[data-nc-fallback]');
    const pagination = root.querySelector('[data-nc-pagination]');
    const active = root.querySelector('[data-nc-active]');
    const sortSelect = root.querySelector('[data-nc-sort]');
    const defaultSort = root.dataset.ncBestsellers === 'true' && root.dataset.defaultSort === 'best-selling' ? 'best-selling' : 'manual';
    const supportedSorts = new Set(SORTS);
    if (defaultSort === 'best-selling') { supportedSorts.add('best-selling'); supportedSorts.delete('manual'); }
    if (!grid || !controls || !status || !count) return;

    let products = [];
    let ready = false;
    let disposed = false;
    let requestNumber = 0;
    let renderController;
    let renderedState;
    let desiredState;
    let shown = PAGE_SIZE;
    let lastSuccessfulURL = location.href;
    const lifecycle = new AbortController();
    const cardCache = new Map();
    const universe = { category: new Map(), type: new Map(), vendor: new Map() };
    const expectedTotal = Number(root.dataset.total);

    grid.querySelectorAll('[data-nc-product], [data-nc-card]').forEach(card => {
      if (card.dataset.productId) cardCache.set(String(card.dataset.productId), card);
    });

    function announce(message, error = false) {
      status.textContent = message;
      status.classList.toggle('is-error', error);
      status.dataset.ncError = error ? 'true' : 'false';
      status.setAttribute('role', error ? 'alert' : 'status');
    }

    function endpoint(path, view) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('The collection endpoint must be on this store.');
      url.search = '';
      const current = new URL(location.href);
      PREVIEW_PARAMS.forEach(name => {
        if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
      });
      url.searchParams.set('view', view);
      return url;
    }

    async function getJSON(url, signal) {
      const response = await fetch(url.href, { credentials: 'same-origin', signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`The store returned ${response.status}.`);
      return response.json();
    }

    function normaliseProduct(record, index) {
      if (!record || record.id === undefined || !record.handle || !Array.isArray(record.variants) || !record.variants.length) throw new Error('The collection index is incomplete.');
      const variants = record.variants.map(variant => {
        const price = Number(variant.price);
        if (!Number.isFinite(price) || price < 0 || typeof variant.available !== 'boolean') throw new Error('A product variant is incomplete.');
        return { id: String(variant.id), price, available: variant.available };
      });
      const priceMin = Number(record.price_min);
      const createdNumber = Number(record.created);
      return {
        id: String(record.id), handle: String(record.handle), title: String(record.title || ''),
        vendor: String(record.vendor || '').trim(), vendorKey: keyOf(record.vendor),
        type: String(record.type || '').trim(), typeKey: typeKeyOf(record.type),
        categories: unique((Array.isArray(record.categories) ? record.categories : []).filter(key => CATEGORIES.has(key))),
        created: Number.isFinite(createdNumber) ? createdNumber : (Date.parse(record.created) || 0) / 1000,
        price: Number.isFinite(priceMin) ? priceMin : Math.min(...variants.map(variant => variant.price)),
        variants, rank: index
      };
    }

    async function loadIndex() {
      const firstURL = endpoint(root.dataset.collectionUrl, 'cocomo-newly-curated-index');
      firstURL.searchParams.set('sort_by', defaultSort);
      firstURL.searchParams.set('page', '1');
      const first = await getJSON(firstURL, lifecycle.signal);
      const total = Number(first.total);
      const pages = Number(first.pages);
      if (!Array.isArray(first.items) || !Number.isInteger(total) || total < 0 || !Number.isInteger(pages) || pages < 0 || Number(first.page) !== 1) throw new Error('The collection index could not be verified.');
      if (pages !== Math.ceil(total / INDEX_PAGE_SIZE) && !(total === 0 && pages === 1)) throw new Error('The collection index has incomplete pages.');
      if (Number.isFinite(expectedTotal) && expectedTotal >= 0 && total !== expectedTotal) throw new Error('The collection changed while loading. Please refresh.');
      const responses = [first];
      for (let page = 2; page <= pages; page += 3) {
        const batch = [];
        for (let next = page; next < Math.min(page + 3, pages + 1); next++) {
          const url = new URL(firstURL.href);
          url.searchParams.set('page', String(next));
          batch.push(getJSON(url, lifecycle.signal).then(result => {
            if (!Array.isArray(result.items) || Number(result.total) !== total || Number(result.pages) !== pages || Number(result.page) !== next || JSON.stringify(result.collection) !== JSON.stringify(first.collection)) throw new Error('The collection pages do not match.');
            return result;
          }));
        }
        responses.push(...await Promise.all(batch));
      }
      const records = responses.flatMap(response => response.items);
      const ids = new Set(records.map(record => String(record.id)));
      if (records.length !== total || ids.size !== total) throw new Error('The complete collection could not be loaded.');
      products = records.map(normaliseProduct);
      products.forEach(product => {
        product.categories.forEach(key => universe.category.set(key, CATEGORIES.get(key)));
        if (product.typeKey) universe.type.set(product.typeKey, typeLabel(product.type));
        if (product.vendorKey && !universe.vendor.has(product.vendorKey)) universe.vendor.set(product.vendorKey, product.vendor);
      });
    }

    function readState() {
      const params = new URL(location.href).searchParams;
      const state = {
        category: unique(params.getAll(PARAMS.category).filter(value => universe.category.has(value))),
        type: unique([...params.getAll(PARAMS.type), ...params.getAll('filter.p.product_type')].map(typeKeyOf).filter(value => universe.type.has(value))),
        vendor: unique(params.getAll(PARAMS.vendor).map(keyOf).filter(value => universe.vendor.has(value))),
        stock: null,
        min: priceValue(params.get(PARAMS.min)), max: priceValue(params.get(PARAMS.max)),
        sort: supportedSorts.has(params.get('sort_by')) ? params.get('sort_by') : defaultSort
      };
      const stocks = params.getAll(PARAMS.stock).filter(value => value === '0' || value === '1');
      state.stock = stocks.length ? stocks[stocks.length - 1] : null;
      if (state.min !== null && state.max !== null && state.min > state.max) state.min = state.max = null;
      return state;
    }

    function stateURL(state) {
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach(name => {
        if (name.startsWith('filter.') || name.startsWith('nc_') || ['page', 'sort_by', 'section_id', 'sections'].includes(name)) url.searchParams.delete(name);
      });
      ['category', 'type', 'vendor'].forEach(facet => state[facet].forEach(value => url.searchParams.append(PARAMS[facet], facet === 'vendor' ? universe.vendor.get(value) : value)));
      if (state.stock !== null) url.searchParams.set(PARAMS.stock, state.stock);
      if (state.min !== null) url.searchParams.set(PARAMS.min, String(state.min / 100));
      if (state.max !== null) url.searchParams.set(PARAMS.max, String(state.max / 100));
      url.searchParams.set('sort_by', state.sort);
      return url;
    }

    function variantMatches(variant, state, ignore) {
      return (ignore === 'stock' || state.stock === null || variant.available === (state.stock === '1')) &&
        (ignore === 'price' || ((state.min === null || variant.price >= state.min) && (state.max === null || variant.price <= state.max)));
    }

    function matches(product, state, ignore) {
      if (ignore !== 'category' && state.category.length && !state.category.some(key => product.categories.includes(key))) return false;
      if (ignore !== 'type' && state.type.length && !state.type.includes(product.typeKey)) return false;
      if (ignore !== 'vendor' && state.vendor.length && !state.vendor.includes(product.vendorKey)) return false;
      return product.variants.some(variant => variantMatches(variant, state, ignore));
    }

    function filteredProducts(state) {
      const selected = products.filter(product => matches(product, state));
      const compare = {
        manual: (a, b) => a.rank - b.rank,
        'best-selling': (a, b) => a.rank - b.rank,
        'created-descending': (a, b) => b.created - a.created || a.rank - b.rank,
        'price-ascending': (a, b) => a.price - b.price || a.rank - b.rank,
        'price-descending': (a, b) => b.price - a.price || a.rank - b.rank,
        'title-ascending': (a, b) => collator.compare(a.title, b.title) || a.rank - b.rank,
        'title-descending': (a, b) => collator.compare(b.title, a.title) || a.rank - b.rank
      }[state.sort];
      return selected.sort(compare);
    }

    function facetCounts(state, facet) {
      const counts = new Map();
      products.forEach(product => {
        if (!matches(product, state, facet)) return;
        let values;
        if (facet === 'category') values = product.categories;
        else if (facet === 'stock') values = unique(product.variants.filter(variant => variantMatches(variant, state, 'stock')).map(variant => variant.available ? '1' : '0'));
        else values = [product[`${facet}Key`]];
        values.filter(Boolean).forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
      });
      return counts;
    }

    function makeChoice(facet, value, label, amount, checked) {
      const row = document.createElement('label');
      row.className = 'nc-choice';
      row.dataset.ncChoiceLabel = keyOf(label);
      const input = document.createElement('input');
      input.type = facet === 'stock' ? 'radio' : 'checkbox';
      input.name = `nc-choice-${root.dataset.sectionId || 'collection'}-${facet}`;
      input.value = value;
      input.checked = checked;
      input.disabled = amount === 0 && !checked;
      input.dataset.ncChoice = facet;
      const name = document.createElement('span');
      name.className = 'nc-choice-name';
      name.textContent = label;
      const countLabel = document.createElement('span');
      countLabel.className = 'nc-choice-count';
      countLabel.textContent = String(amount);
      row.append(input, name, countLabel);
      row.classList.toggle('is-selected', checked);
      return row;
    }

    function searchChoices(facet) {
      const container = root.querySelector(`[data-nc-options="${facet}"]`);
      if (!container) return;
      const query = keyOf(root.querySelector(`[data-nc-search="${facet}"]`)?.value);
      let visible = 0;
      container.querySelectorAll('[data-nc-choice-label]').forEach(row => {
        row.hidden = query !== '' && !row.dataset.ncChoiceLabel.includes(query);
        if (!row.hidden) visible++;
      });
      const message = container.querySelector('.nc-no-match');
      if (message) message.hidden = visible > 0;
    }

    function renderControls(state) {
      const focused = document.activeElement;
      const focusedChoice = focused?.dataset.ncChoice ? { facet: focused.dataset.ncChoice, value: focused.value } : null;
      ['category', 'type', 'vendor', 'stock'].forEach(facet => {
        const container = root.querySelector(`[data-nc-options="${facet}"]`);
        if (!container) return;
        const scrollTop = container.scrollTop;
        const counts = facetCounts(state, facet);
        const fragment = document.createDocumentFragment();
        if (facet === 'stock') {
          const total = products.filter(product => matches(product, state, 'stock')).length;
          fragment.append(makeChoice(facet, '', 'All availability', total, state.stock === null));
          [['1', 'In stock'], ['0', 'Out of stock']].forEach(([value, label]) => {
            if ((counts.get(value) || 0) > 0 || state.stock === value) fragment.append(makeChoice(facet, value, label, counts.get(value) || 0, state.stock === value));
          });
        } else {
          const values = [...universe[facet]].filter(([value]) => (counts.get(value) || 0) > 0 || state[facet].includes(value));
          if (facet === 'category') values.sort((a, b) => [...CATEGORIES.keys()].indexOf(a[0]) - [...CATEGORIES.keys()].indexOf(b[0]));
          else values.sort((a, b) => collator.compare(a[1], b[1]));
          values.forEach(([value, label]) => fragment.append(makeChoice(facet, value, label, counts.get(value) || 0, state[facet].includes(value))));
        }
        const noMatch = document.createElement('p');
        noMatch.className = 'nc-no-match';
        noMatch.textContent = 'No matching options. Try another search or clear a filter.';
        noMatch.hidden = true;
        fragment.append(noMatch);
        container.replaceChildren(fragment);
        searchChoices(facet);
        container.scrollTop = scrollTop;
      });
      root.querySelectorAll('[data-nc-dropdown]').forEach(dropdown => {
        const facet = dropdown.dataset.ncDropdown;
        const size = ['category', 'type', 'vendor'].includes(facet) ? state[facet].length : facet === 'stock' ? Number(state.stock !== null) : Number(state.min !== null || state.max !== null);
        dropdown.classList.toggle('has-selection', size > 0);
        let selectedCount = dropdown.querySelector('[data-nc-selected-count]');
        if (!selectedCount) {
          selectedCount = document.createElement('span');
          selectedCount.dataset.ncSelectedCount = '';
          selectedCount.className = 'nc-dropdown-count';
          const summary = dropdown.querySelector('summary');
          if (summary) summary.insertBefore(selectedCount, summary.querySelector('.nc-caret'));
        }
        selectedCount.textContent = size ? `(${size})` : '';
        selectedCount.hidden = size === 0;
      });
      if (sortSelect) sortSelect.value = state.sort;
      ['min', 'max'].forEach(bound => {
        const input = root.querySelector(`[name="${PARAMS[bound]}"]`);
        if (input && input !== document.activeElement) input.value = state[bound] === null ? '' : String(state[bound] / 100);
      });
      renderActive(state);
      if (focusedChoice && !focused.isConnected) {
        [...root.querySelectorAll('[data-nc-choice]')].find(input => input.dataset.ncChoice === focusedChoice.facet && input.value === focusedChoice.value)?.focus({ preventScroll: true });
      }
    }

    function renderActive(state) {
      if (!active) return;
      const fragment = document.createDocumentFragment();
      const add = (facet, value, label) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nc-active-filter';
        button.dataset.ncRemove = facet;
        button.dataset.value = value;
        button.setAttribute('aria-label', `Remove ${label} filter`);
        button.append(document.createTextNode(label));
        const cross = document.createElement('span');
        cross.setAttribute('aria-hidden', 'true');
        cross.textContent = '×';
        button.append(cross);
        fragment.append(button);
      };
      ['category', 'type', 'vendor'].forEach(facet => state[facet].forEach(value => add(facet, value, universe[facet].get(value))));
      if (state.stock !== null) add('stock', state.stock, state.stock === '1' ? 'In stock' : 'Out of stock');
      if (state.min !== null || state.max !== null) {
        const format = cents => new Intl.NumberFormat('en-SG', { style: 'currency', currency: root.dataset.currency || 'SGD', maximumFractionDigits: 2 }).format(cents / 100);
        add('price', '', state.min !== null && state.max !== null ? `${format(state.min)} – ${format(state.max)}` : state.min !== null ? `From ${format(state.min)}` : `Up to ${format(state.max)}`);
      }
      active.replaceChildren(fragment);
      active.hidden = !active.childElementCount;
      root.querySelectorAll('[data-nc-clear]').forEach(button => {
        button.hidden = active.childElementCount === 0;
      });
    }

    async function loadCards(list, signal) {
      const missing = list.filter(product => !cardCache.has(product.id));
      for (let start = 0; start < missing.length; start += CARD_BATCH_SIZE * 2) {
        const batches = [];
        for (let next = start; next < Math.min(start + CARD_BATCH_SIZE * 2, missing.length); next += CARD_BATCH_SIZE) {
          const batch = missing.slice(next, next + CARD_BATCH_SIZE);
          const url = endpoint(root.dataset.searchUrl || '/search', 'cocomo-newly-curated-cards');
          url.searchParams.set('type', 'product');
          url.searchParams.set('q', batch.map(product => product.handle).join(','));
          batches.push(getJSON(url, signal).then(response => {
            if (!Array.isArray(response.items)) throw new Error('Product cards are unavailable.');
            const returned = new Map();
            response.items.forEach(item => {
              if (item && item.id !== undefined && typeof item.html === 'string') returned.set(String(item.id), item.html);
            });
            batch.forEach(product => {
              const html = returned.get(product.id);
              if (!html) throw new Error('Some product cards could not be loaded.');
              const template = document.createElement('template');
              template.innerHTML = html.trim();
              const card = template.content.querySelector('[data-nc-product], [data-nc-card], .nc-product-card');
              if (!card || String(card.dataset.productId) !== product.id) throw new Error('A product card could not be verified.');
              cardCache.set(product.id, card);
            });
          }));
        }
        await Promise.all(batches);
      }
    }

    async function apply(state, options = {}) {
      desiredState = cloneState(state);
      const sequence = ++requestNumber;
      renderController?.abort();
      renderController = new AbortController();
      const selected = filteredProducts(state);
      const nextShown = Math.min(options.shown || PAGE_SIZE, Math.max(PAGE_SIZE, selected.length));
      const visible = selected.slice(0, nextShown);
      const oldShown = grid.querySelectorAll('[data-nc-product], [data-nc-card]').length;
      grid.setAttribute('aria-busy', 'true');
      if (more) more.disabled = true;
      announce(options.append ? 'Loading more products…' : 'Updating products…');
      try {
        await loadCards(visible, renderController.signal);
        if (sequence !== requestNumber || disposed) return;
        grid.replaceChildren(...visible.map(product => cardCache.get(product.id)));
        renderedState = cloneState(state);
        desiredState = cloneState(state);
        shown = nextShown;
        renderControls(state);
        count.textContent = `${selected.length} ${selected.length === 1 ? 'product' : 'products'}`;
        if (empty) empty.hidden = selected.length !== 0;
        if (more) { more.hidden = nextShown >= selected.length; more.disabled = false; }
        if (pagination) pagination.hidden = true;
        if (fallback) fallback.hidden = true;
        controls.hidden = false;
        controls.removeAttribute('inert');
        controls.querySelectorAll('[data-nc-initial-disabled]').forEach(control => { control.disabled = false; control.removeAttribute('data-nc-initial-disabled'); });
        root.dataset.ncCatalogReady = 'true';
        ready = true;
        const url = stateURL(state);
        const historyState = { ...history.state, ncCollection: root.dataset.sectionId, ncShown: shown };
        if (options.history === 'push' && url.href !== location.href) history.pushState(historyState, '', url.href);
        else history.replaceState(historyState, '', url.href);
        lastSuccessfulURL = url.href;
        announce(`Showing ${visible.length} of ${selected.length} products.`);
        root.dispatchEvent(new CustomEvent('cocomo:collection:updated', { bubbles: true, detail: { sectionId: root.dataset.sectionId, append: !!options.append, shown: visible.length, total: selected.length } }));
        if (options.append) visible[oldShown] && cardCache.get(visible[oldShown].id)?.querySelector('a')?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== requestNumber || disposed) return;
        if (renderedState) {
          desiredState = cloneState(renderedState);
          renderControls(renderedState);
          history.replaceState({ ...history.state, ncShown: shown }, '', lastSuccessfulURL);
          announce('These filters could not be applied. Your previous results are still shown. Please try again.', true);
        } else {
          controls.hidden = true;
          if (fallback) fallback.hidden = false;
          announce('The complete collection could not be loaded. The original products and standard filters are still available. Please refresh to try again.', true);
        }
        console.warn('[COCOMO Newly Curated]', error.message);
      } finally {
        if (sequence === requestNumber) { grid.removeAttribute('aria-busy'); if (more) more.disabled = false; }
      }
    }

    root.addEventListener('change', event => {
      if (!ready) return;
      if (event.target.matches('[data-nc-choice]')) {
        const input = event.target;
        const state = cloneState(desiredState);
        const facet = input.dataset.ncChoice;
        if (facet === 'stock') state.stock = input.value || null;
        else state[facet] = input.checked ? unique([...state[facet], input.value]) : state[facet].filter(value => value !== input.value);
        apply(state, { history: 'push' });
      } else if (event.target.matches('[data-nc-sort]')) {
        const state = cloneState(desiredState);
        state.sort = supportedSorts.has(event.target.value) ? event.target.value : defaultSort;
        apply(state, { history: 'push' });
      }
    }, { signal: lifecycle.signal });

    root.addEventListener('input', event => {
      if (event.target.matches('[data-nc-search]')) searchChoices(event.target.dataset.ncSearch);
      if (event.target.matches('[data-nc-price-form] input')) event.target.setCustomValidity('');
    }, { signal: lifecycle.signal });

    root.addEventListener('submit', event => {
      if (!event.target.matches('[data-nc-price-form]')) return;
      event.preventDefault();
      if (!ready) return;
      const state = cloneState(desiredState);
      const minInput = event.target.querySelector(`[name="${PARAMS.min}"]`);
      const maxInput = event.target.querySelector(`[name="${PARAMS.max}"]`);
      state.min = priceValue(minInput?.value);
      state.max = priceValue(maxInput?.value);
      if (state.min !== null && state.max !== null && state.min > state.max) {
        maxInput.setCustomValidity('The maximum price must be equal to or greater than the minimum price.');
        maxInput.reportValidity();
        return;
      }
      apply(state, { history: 'push' });
    }, { signal: lifecycle.signal });

    root.addEventListener('click', event => {
      const button = event.target.closest('[data-nc-clear], [data-nc-remove], [data-nc-more]');
      if (!button || !root.contains(button) || !ready) return;
      event.preventDefault();
      const state = cloneState(desiredState);
      if (button.matches('[data-nc-more]')) { apply(state, { shown: shown + PAGE_SIZE, append: true }); return; }
      if (button.matches('[data-nc-clear]')) {
        state.category = []; state.type = []; state.vendor = []; state.stock = null; state.min = null; state.max = null;
        root.querySelectorAll('[data-nc-search]').forEach(input => { input.value = ''; });
      } else {
        const facet = button.dataset.ncRemove;
        if (facet === 'price') state.min = state.max = null;
        else if (facet === 'stock') state.stock = null;
        else state[facet] = state[facet].filter(value => value !== button.dataset.value);
      }
      apply(state, { history: 'push' });
    }, { signal: lifecycle.signal });

    root.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const dropdown = event.target.closest('[data-nc-dropdown]');
      if (dropdown?.open) { dropdown.open = false; dropdown.querySelector('summary')?.focus(); }
    }, { signal: lifecycle.signal });

    root.addEventListener('toggle', event => {
      if (!event.target.matches('[data-nc-dropdown]') || !event.target.open) return;
      root.querySelectorAll('[data-nc-dropdown][open]').forEach(dropdown => { if (dropdown !== event.target) dropdown.open = false; });
    }, { capture: true, signal: lifecycle.signal });

    document.addEventListener('click', event => {
      root.querySelectorAll('[data-nc-dropdown][open]').forEach(dropdown => { if (!dropdown.contains(event.target)) dropdown.open = false; });
    }, { signal: lifecycle.signal });

    window.addEventListener('popstate', event => {
      if (!ready || location.pathname !== new URL(root.dataset.collectionUrl, location.origin).pathname) return;
      apply(readState(), { shown: Number(event.state?.ncShown) || PAGE_SIZE });
    }, { signal: lifecycle.signal });

    document.addEventListener('shopify:section:unload', event => {
      if (event.detail?.sectionId !== root.dataset.sectionId && !event.target.contains(root)) return;
      disposed = true;
      renderController?.abort();
      lifecycle.abort();
    }, { signal: lifecycle.signal });

    if (sortSelect) [...sortSelect.options].forEach(option => { if (!supportedSorts.has(option.value)) option.remove(); });
    controls.querySelectorAll(':disabled').forEach(control => { control.dataset.ncInitialDisabled = ''; });
    controls.hidden = true;
    announce('Loading all collection filters…');
    loadIndex().then(() => {
      if (!disposed) return apply(readState());
    }).catch(error => {
      if (error.name === 'AbortError' || disposed) return;
      controls.hidden = true;
      if (fallback) fallback.hidden = false;
      root.dataset.ncCatalogReady = 'error';
      announce('The complete collection filters could not be loaded. The original products and standard filters are still available. Please refresh to try again.', true);
      console.warn('[COCOMO Newly Curated]', error.message);
    });
  }

  function initialiseAll() { document.querySelectorAll('[data-nc-root]').forEach(initialise); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  else initialiseAll();
  document.addEventListener('shopify:section:load', initialiseAll);
})();
